import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@rook/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@rook/shared/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@rook/shared/components/ui/form";
import { Input } from "@rook/shared/components/ui/input";
import { usePageTitle } from "@rook/shared/lib/usePageTitle";

import { forgotPassword } from "./api";
import { forgotPasswordSchema, type ForgotPasswordInput } from "./schemas";

export const ForgotPasswordView = () => {
  usePageTitle("Forgot password");
  const navigate = useNavigate();
  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const mutation = useMutation({
    mutationFn: forgotPassword,
    onSuccess: (_data, variables) => {
      // Forward the email to the reset page so the user doesn't
      // type it again. Code is sent via email.
      navigate(
        `/forgot-password/reset?email=${encodeURIComponent(variables.email)}`,
      );
    },
  });

  const errorMessage =
    mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <Card>
      <CardHeader>
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          Recover access
        </p>
        <CardTitle className="text-2xl">Forgot password</CardTitle>
        <CardDescription>
          Enter the email tied to your portal account. We&apos;ll send a 6-character
          verification code so you can set a new password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {errorMessage && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="name@mail.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" size="lg" disabled={mutation.isPending}>
              {mutation.isPending ? "Sending…" : "Send code"}
            </Button>
          </form>
        </Form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Remembered it?{" "}
          <Link to="/" className="font-semibold text-rook-light hover:underline">
            Back to sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
